/**
 * WebSocket Client Configuration
 *
 * Socket.io 클라이언트 설정 및 인스턴스 관리
 */

import { io, Socket } from 'socket.io-client';
import { tokenManager, onTokenRefreshed, refreshAccessToken, registerSocketIdProvider } from './api';
import { ServerEvents, ClientEvents, InspectionViewer } from '../types/websocket';
import type { LiveResourceType } from '@safetyhub/shared';

// WebSocket 서버 URL
const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';
const WS_NAMESPACE = '/ws';

/**
 * Socket.io 클라이언트 인스턴스
 */
let socket: Socket | null = null;

// 현재 WebSocket 연결의 socket.id (연결 시 갱신, 연결 해제 시 null)
let currentSocketId: string | null = null;

/**
 * 현재 WebSocket 연결의 socket.id 반환.
 * HTTP 요청 시 X-Socket-Id 헤더로 전달하여 서버 push 토스트 중복을 방지.
 */
export function getCurrentSocketId(): string | null {
  return currentSocketId;
}

// api.ts에 socket ID 제공자 등록 (순환 참조 방지를 위한 콜백 방식)
registerSocketIdProvider(() => currentSocketId);

// 토큰 갱신 리스너 정리 함수
let unsubscribeTokenRefreshed: (() => void) | null = null;

// WebSocket 인증 에러로 인한 토큰 갱신 진행 중 여부 (무한 루프 방지)
let isWsRefreshing = false;

// 절전 복귀 핸들러
let visibilityChangeHandler: (() => void) | null = null;

/**
 * 연결 옵션
 */
const getConnectionOptions = () => {
  const token = tokenManager.getCurrentToken();

  return {
    path: '/socket.io',
    transports: ['websocket', 'polling'] as ('websocket' | 'polling')[],
    auth: {
      token,
    },
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 20, // 최대 20회 재시도 (약 10분)
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  };
};

/**
 * Socket.io 클라이언트 생성/가져오기
 * 생성 시 reconnect_attempt 이벤트에서 최신 토큰을 auth에 반영합니다.
 */
export const getSocket = (): Socket => {
  if (!socket) {
    socket = io(`${WS_URL}${WS_NAMESPACE}`, getConnectionOptions());

    // 연결/해제 시 currentSocketId 갱신
    socket.on('connect', () => {
      currentSocketId = socket?.id ?? null;
    });
    socket.on('disconnect', () => {
      currentSocketId = null;
    });

    // 재연결 시도마다 최신 토큰으로 auth 갱신
    // (HTTP 토큰 갱신 후 새 토큰이 localStorage에 저장되어 있을 수 있음)
    socket.io.on('reconnect_attempt', () => {
      const token = tokenManager.getCurrentToken();
      socket!.auth = { token };
    });

    // 서버 인증 거부 시 토큰 갱신 후 재연결
    // 서버가 handleConnection에서 JWT 검증 실패 → emit('error') + disconnect() 하므로
    // 'error' 이벤트(ServerEvents.CONNECTION_ERROR)로 감지
    socket.on('error', async (err: any) => {
      const message = typeof err === 'string' ? err : err?.message;
      const isAuthError =
        message === 'Authentication failed' ||
        message?.includes('expired') ||
        message?.includes('jwt');

      if (!isAuthError || isWsRefreshing) return;

      isWsRefreshing = true;
      console.log('[WebSocket] Server auth error, attempting token refresh...');

      try {
        const newToken = await refreshAccessToken();
        if (newToken && socket) {
          console.log('[WebSocket] Token refreshed, reconnecting...');
          socket.auth = { token: newToken };
          socket.connect();
        } else {
          console.error('[WebSocket] Token refresh failed');
        }
      } finally {
        isWsRefreshing = false;
      }
    });

    // HTTP API 토큰 갱신 완료 시 WebSocket도 새 토큰으로 재연결
    // (토큰 만료로 Authentication failed 후 복구)
    unsubscribeTokenRefreshed = onTokenRefreshed((newToken) => {
      if (socket && !socket.connected) {
        console.log('[WebSocket] Token refreshed, reconnecting with new token...');
        socket.auth = { token: newToken };
        socket.connect();
      } else if (socket && socket.connected) {
        // 연결 중이지만 auth 갱신 (다음 reconnect_attempt 대비)
        socket.auth = { token: newToken };
      }
    });

    // 절전/백그라운드 복귀 시 재연결 처리
    visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible' && socket && !socket.connected) {
        const token = tokenManager.getCurrentToken();
        if (token) {
          console.log('[WebSocket] Page visible again, reconnecting...');
          socket.auth = { token };
          // 재시도 카운터 리셋 — 장시간 절전 후에도 즉시 재연결 가능
          (socket.io as unknown as { reconnectionAttempts?: (n: number) => void }).reconnectionAttempts?.(20);
          (socket.io as unknown as { backoff?: { reset: () => void } }).backoff?.reset();
          socket.connect();
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityChangeHandler);
  }
  return socket;
};

/**
 * WebSocket 연결
 */
export const connectWebSocket = (): Socket => {
  const sock = getSocket();

  if (!sock.connected) {
    // 연결 전 토큰 업데이트
    const token = tokenManager.getCurrentToken();
    sock.auth = { token };
    sock.connect();
  }

  return sock;
};

/**
 * WebSocket 연결 해제
 */
export const disconnectWebSocket = (): void => {
  isWsRefreshing = false;
  currentSocketId = null;
  // 리스너 정리를 socket = null 이전에 수행 (socket 참조가 유효한 동안 정리)
  if (unsubscribeTokenRefreshed) {
    unsubscribeTokenRefreshed();
    unsubscribeTokenRefreshed = null;
  }
  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * 토큰 변경 시 재연결
 */
export const reconnectWithNewToken = (): void => {
  if (socket) {
    socket.disconnect();
    const token = tokenManager.getCurrentToken();
    socket.auth = { token };
    socket.connect();
  }
};

/**
 * Room 입장/퇴장 헬퍼
 */
export const joinOrganization = (companyId: string): Promise<{ success: boolean; message?: string }> => {
  return new Promise((resolve) => {
    const sock = getSocket();
    if (sock.connected) {
      sock.emit(ClientEvents.JOIN_ORGANIZATION, companyId, (response: { success: boolean; message?: string }) => {
        resolve(response);
      });
    } else {
      resolve({ success: false, message: 'Not connected' });
    }
  });
};

export const leaveOrganization = (companyId: string): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.LEAVE_ORGANIZATION, companyId);
  }
};

/**
 * LIVE Room 입장 — 상세 페이지 실시간 동기화용.
 *
 * 서버(`handleJoinLive`)가 리소스를 조회해 조직을 알아낸 뒤 멤버십을 검증하므로,
 * 실패는 조용히 무시하지 말고 호출부가 응답을 확인할 수 있게 Promise로 돌려준다.
 */
export const joinLive = (
  resourceType: LiveResourceType,
  resourceId: string,
): Promise<{ success: boolean; message?: string }> => {
  return new Promise((resolve) => {
    const sock = getSocket();
    if (sock.connected) {
      sock.emit(
        ClientEvents.JOIN_LIVE,
        { resourceType, resourceId },
        (response: { success: boolean; message?: string }) => {
          resolve(response);
        },
      );
    } else {
      resolve({ success: false, message: 'Not connected' });
    }
  });
};

export const leaveLive = (resourceType: LiveResourceType, resourceId: string): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.LEAVE_LIVE, { resourceType, resourceId });
  }
};

export const joinSite = (siteId: string): Promise<{ success: boolean; message?: string }> => {
  return new Promise((resolve) => {
    const sock = getSocket();
    if (sock.connected) {
      sock.emit(ClientEvents.JOIN_SITE, siteId, (response: { success: boolean; message?: string }) => {
        resolve(response);
      });
    } else {
      resolve({ success: false, message: 'Not connected' });
    }
  });
};

export const leaveSite = (siteId: string): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.LEAVE_SITE, siteId);
  }
};

export interface JoinInspectionResult {
  success: boolean;
  message?: string;
  viewers?: InspectionViewer[];
}

export const joinInspection = (inspectionId: string): Promise<JoinInspectionResult> => {
  return new Promise((resolve) => {
    const sock = getSocket();
    if (sock.connected) {
      sock.emit(ClientEvents.JOIN_INSPECTION, inspectionId, (response: JoinInspectionResult) => {
        resolve(response);
      });
    } else {
      resolve({ success: false, message: 'Not connected' });
    }
  });
};

export const leaveInspection = (inspectionId: string): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.LEAVE_INSPECTION, inspectionId);
  }
};

/**
 * 알림 동기화 - 단일 알림 읽음 처리
 */
export interface NotificationIdentifier {
  type: string;
  createdAt: number;
  dataKey?: string;
}

export const emitNotificationRead = (notification: NotificationIdentifier): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.MARK_NOTIFICATION_READ, notification);
  }
};

/**
 * 알림 동기화 - 모든 알림 읽음 처리
 */
export const emitAllNotificationsRead = (): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.MARK_ALL_NOTIFICATIONS_READ);
  }
};

/**
 * 알림 동기화 - 모든 알림 삭제
 */
export const emitNotificationsCleared = (): void => {
  const sock = getSocket();
  if (sock.connected) {
    sock.emit(ClientEvents.CLEAR_ALL_NOTIFICATIONS);
  }
};

// 이벤트 타입 re-export
export { ServerEvents, ClientEvents };
